import { useParams, Link } from 'react-router-dom';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { useEffect, useState } from 'react';
import { storage } from '../../lib/firebase.js';
import { useDoc } from '../../lib/hooks.js';
import { categoryLabel, formatBytes, formatTimestamp } from '../../lib/format.js';

interface DocData {
  originalName?: string;
  mimeType?: string;
  sizeBytes?: number;
  storagePath?: string;
  category?: string;
  docType?: string;
  entityIds?: string[];
  parties?: string[];
  year?: number;
  summary?: string;
  keyFields?: Record<string, unknown>;
  status?: string;
  errorReason?: string;
  uploadedAt?: { seconds: number };
  llm?: {
    model?: string;
    promptVersion?: string;
    confidence?: number;
    inputTokens?: number;
    outputTokens?: number;
    cacheReadTokens?: number;
  };
}

export function DocumentDetail() {
  const { docId } = useParams<{ docId: string }>();
  const { data, loading } = useDoc<DocData>('documents', docId);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  useEffect(() => {
    if (data?.storagePath) {
      getDownloadURL(storageRef(storage, data.storagePath))
        .then(setDownloadUrl)
        .catch(() => setDownloadUrl(null));
    }
  }, [data?.storagePath]);

  if (loading)
    return (
      <div className="mx-auto w-full max-w-5xl px-6 py-6 text-sm text-stone-400">Loading…</div>
    );
  if (!data)
    return (
      <div className="mx-auto w-full max-w-5xl px-6 py-6 text-sm text-stone-400">Not found.</div>
    );

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 px-6 py-6">
      <header>
        <Link to="/documents" className="text-xs text-stone-500 hover:underline">
          ← All documents
        </Link>
        <h1 className="mt-2 text-2xl font-bold tracking-tight">{data.originalName ?? docId}</h1>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
          <span className="chip">{categoryLabel(data.category ?? 'other')}</span>
          {data.docType && <span className="chip">{data.docType}</span>}
          <span className="text-stone-500">{formatBytes(data.sizeBytes ?? 0)}</span>
          <span className="text-stone-500">{data.mimeType}</span>
          <span className="text-stone-500">Uploaded {formatTimestamp(data.uploadedAt)}</span>
        </div>
      </header>

      {data.status !== 'ready' && data.status !== 'triage' && (
        <div className="card bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Status: <strong>{data.status}</strong>
          {data.errorReason && <div className="mt-1 text-xs">{data.errorReason}</div>}
        </div>
      )}

      <section className="card p-4">
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-stone-500">
          Summary
        </h2>
        <p className="text-sm leading-relaxed text-stone-900">
          {data.summary ?? <span className="text-stone-400">Not yet summarized.</span>}
        </p>
      </section>

      <section className="card p-4">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-stone-500">
          Extracted fields
        </h2>
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <Field label="Year" value={data.year?.toString()} />
          <Field label="Entities" value={(data.entityIds ?? []).join(', ')} />
          <Field label="Parties" value={(data.parties ?? []).join(', ')} />
          {Object.entries(data.keyFields ?? {}).map(([k, v]) => (
            <Field key={k} label={k} value={String(v ?? '')} />
          ))}
        </dl>
      </section>

      {downloadUrl && (
        <a
          href={downloadUrl}
          className="btn-secondary inline-flex"
          target="_blank"
          rel="noreferrer"
        >
          Download original
        </a>
      )}

      {data.llm && (
        <section className="card p-4 text-xs text-stone-500">
          <h2 className="mb-2 font-semibold uppercase tracking-wider">Classification audit</h2>
          <div className="grid grid-cols-2 gap-2">
            <div>Model: <span className="text-stone-700">{data.llm.model}</span></div>
            <div>Prompt: <span className="text-stone-700">{data.llm.promptVersion}</span></div>
            <div>
              Confidence:{' '}
              <span className="text-stone-700">
                {data.llm.confidence != null
                  ? `${(data.llm.confidence * 100).toFixed(0)}%`
                  : '—'}
              </span>
            </div>
            <div>
              Tokens:{' '}
              <span className="text-stone-700">
                {data.llm.inputTokens} in / {data.llm.outputTokens} out
                {data.llm.cacheReadTokens
                  ? ` (cache: ${data.llm.cacheReadTokens})`
                  : ''}
              </span>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | undefined }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-stone-500">{label}</dt>
      <dd className="text-stone-900">{value || <span className="text-stone-400">—</span>}</dd>
    </div>
  );
}
